import { useEffect, useState } from 'react';
import { enterpriseLicense, getDeviceId, storeToken, enterpriseToken } from './license';
import { applyProfiles, getMode, getSub } from './profiles';

// company policy (signed into the enterprise licence) + local choices: hidden branches, pick-up code
const HID = 'giss_hidden', HO = 'giss_handoff_on';
const API = `${process.env.REACT_APP_BACKEND_URL}/api/kritis`;
export const BRANCH_KEYS = ['detect', 'aqua', 'storm', 'pipe', 'building', 'garden', 'photo', 'survey', 'outdoor'];
export const SUB_KEYS = ['water', 'gas', 'cable', 'sewer', 'inhouse', 'locate', 'heat', 'roof'];
const emit = () => window.dispatchEvent(new Event('giss-policy'));

let memo = [null, null];
// cached per stored-token string (tile requests call this very often)
export const companyPolicy = () => {
  const raw = localStorage.getItem('giss_license_tokens');
  if (memo[0] !== raw) memo = [raw, enterpriseLicense()?.pol || null];
  return memo[1];
};
export const kritisOn = () => !!companyPolicy()?.kritis;
const localHidden = () => { try { const h = JSON.parse(localStorage.getItem(HID)); return { branches: h?.branches || [], subs: h?.subs || [] }; } catch { return { branches: [], subs: [] }; } };
export const hidden = () => (kritisOn() ? { branches: companyPolicy().branches || [], subs: companyPolicy().subs || [] } : localHidden());
export const setLocalHidden = (h) => { if (kritisOn()) return; localStorage.setItem(HID, JSON.stringify(h)); emit(); };
export const branchOn = (k) => (k === 'all' ? !kritisOn() && !hidden().branches.length && !hidden().subs.length : !hidden().branches.includes(k));
export const subOn = (s) => branchOn('detect') && !hidden().subs.includes(s);
export const handoffOn = () => !kritisOn() && localStorage.getItem(HO) === '1';
// KRITIS: external services the admin explicitly allowed; own map server; saved offline areas
export const EXT_KEYS = ['tiles', 'elev', 'weather', 'osm', 'route', 'street'];
export const extOk = (g) => !kritisOn() || (companyPolicy().allow || []).includes(g);
export const companyMap = () => (kritisOn() ? companyPolicy().map || null : null);
export const cacheOk = () => !kritisOn() || companyPolicy().cache !== false;
export const setHandoffOn = (v) => { localStorage.setItem(HO, v ? '1' : '0'); emit(); };

// every external service (agents, AI, share codes) asks here first
export function guardExternal() {
  if (!kritisOn()) return;
  window.dispatchEvent(new Event('giss-kritis-block'));
  throw new Error('kritis');
}

// active branch / area hidden -> switch to the first visible one
export function enforceVisible() {
  const m = getMode();
  if (!branchOn(m)) { const k = BRANCH_KEYS.find(branchOn); if (k) applyProfiles([k], k === 'detect' ? SUB_KEYS.find(subOn) : undefined); return; }
  if (m === 'detect' && !subOn(getSub())) { const s = SUB_KEYS.find(subOn); if (s) applyProfiles(['detect'], s); else applyProfiles([BRANCH_KEYS.find((k) => k !== 'detect' && branchOn(k)) || 'all']); }
}
window.addEventListener('giss-policy', enforceVisible);
setTimeout(enforceVisible, 0);

export function usePolicy() {
  const [, setN] = useState(0);
  useEffect(() => { const on = () => setN((n) => n + 1); window.addEventListener('giss-policy', on); return () => window.removeEventListener('giss-policy', on); }, []);
  return { kritis: kritisOn(), hidden: hidden(), handoff: handoffOn() };
}

async function call(path, body) {
  const res = await fetch(`${API}/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: enterpriseToken(), device_id: getDeviceId(), ...body }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'error');
  if (data.token) { storeToken(data.token); emit(); }
  return data;
}
export const kritisStatus = () => call('status', {});
export const kritisSetup = (password, policy) => call('setup', { password, policy });
export const kritisChange = (password, policy, newPassword) => call('policy', { password, policy, ...(newPassword ? { new_password: newPassword } : {}) });
export const kritisProof = (password) => call('proof', { password });
